import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useUsage } from '../context/UsageContext';
import { useIsTenantAdmin } from '../hooks/usePermission';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Progress } from './ui/progress';
import { Button } from './ui/button';
import { Sparkles, HardDrive, AlertTriangle, Package } from 'lucide-react';

const UNLIMITED = -1;

/**
 * Dashboard widget showing AI + storage consumption this month against the
 * tenant's effective ceiling (base plan + active add-ons). Emits a visible
 * low-capacity alert once any resource drops below 30% remaining.
 */
export default function UsageWidget() {
  const { language } = useLanguage();
  const isAdmin = useIsTenantAdmin();
  const navigate = useNavigate();
  const { data } = useUsage();

  const lbl = (es, en) => (language === 'es' ? es : en);

  if (!data) return null;

  const { limits, usage } = data;
  const aiLimit = limits.ai_queries_per_month;
  const storageLimit = limits.storage_gb;
  const aiUsed = usage.ai_queries_this_month || 0;
  const storageUsedGb = usage.storage_gb_used || 0;

  const aiBar = toBar(aiUsed, aiLimit);
  const stBar = toBar(storageUsedGb, storageLimit);

  // Low-capacity alert: triggered when remaining capacity drops at or below
  // 30% (i.e., 70% or more consumed). Ignore unlimited resources.
  const lowResources = [];
  if (aiBar.remainingPct !== null && aiBar.remainingPct <= 30) {
    lowResources.push({
      name: lbl('consultas de IA', 'AI queries'),
      pct: aiBar.remainingPct,
    });
  }
  if (stBar.remainingPct !== null && stBar.remainingPct <= 30) {
    lowResources.push({
      name: lbl('almacenamiento', 'storage'),
      pct: stBar.remainingPct,
    });
  }

  return (
    <Card className="card-base">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-serif flex items-center gap-2">
          <Package className="w-4 h-4 text-bronze" />
          {lbl('Uso este mes', 'Usage this month')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        {lowResources.length > 0 && (
          <div className="rounded-md border border-bronze/40 bg-bronze/5 p-3 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-bronze shrink-0 mt-0.5" />
            <div className="flex-1 text-sm">
              <p className="font-medium text-foreground">
                {lbl('Capacidad por agotarse', 'Capacity running low')}
              </p>
              <p className="text-muted-foreground text-xs mt-0.5">
                {lowResources.map((r, i) => (
                  <span key={r.name}>
                    {i > 0 ? ', ' : ''}
                    {lbl(
                      `${r.pct}% restante en ${r.name}`,
                      `${r.pct}% left on ${r.name}`,
                    )}
                  </span>
                ))}
              </p>
            </div>
            {isAdmin && (
              <Button
                size="sm"
                variant="outline"
                className="shrink-0"
                onClick={() => navigate('/dashboard/settings?tab=addons')}
              >
                {lbl('Add-ons', 'Add-ons')}
              </Button>
            )}
          </div>
        )}

        <UsageRow
          icon={Sparkles}
          iconClass="text-bronze"
          label={lbl('Consultas IA', 'AI queries')}
          bar={aiBar}
          unit=""
          unlimitedLabel={lbl('Ilimitado', 'Unlimited')}
        />
        <UsageRow
          icon={HardDrive}
          iconClass="text-emerald"
          label={lbl('Almacenamiento', 'Storage')}
          bar={stBar}
          unit="GB"
          unlimitedLabel={lbl('Ilimitado', 'Unlimited')}
        />

        {isAdmin && (
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-between -mx-2"
            onClick={() => navigate('/dashboard/settings?tab=addons')}
          >
            <span className="text-xs text-muted-foreground">
              {lbl('Gestionar add-ons', 'Manage add-ons')}
            </span>
            <span>→</span>
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

function UsageRow({ icon: Icon, iconClass, label, bar, unit, unlimitedLabel }) {
  const over = bar.remainingPct !== null && bar.remainingPct <= 0;
  const low = bar.remainingPct !== null && bar.remainingPct <= 30;
  const tint = over ? '[&>div]:bg-destructive' : low ? '[&>div]:bg-bronze' : '';

  return (
    <div>
      <div className="flex items-center justify-between text-sm mb-1.5">
        <span className="flex items-center gap-2 text-muted-foreground">
          <Icon className={`w-4 h-4 ${iconClass}`} />
          {label}
        </span>
        <span
          className={`font-medium tabular-nums ${
            over ? 'text-destructive' : low ? 'text-bronze' : 'text-foreground'
          }`}
        >
          {fmt(bar.used, unit)} / {bar.unlimited ? unlimitedLabel : fmt(bar.limit, unit)}
        </span>
      </div>
      {!bar.unlimited && <Progress value={bar.percent} className={tint} />}
    </div>
  );
}

function toBar(used, limit) {
  if (limit === UNLIMITED || limit == null) {
    return { used, limit, unlimited: true, percent: 0, remainingPct: null };
  }
  const safe = Math.max(limit, 1);
  const percent = Math.min(100, Math.round((used / safe) * 100));
  const remainingPct = Math.max(0, 100 - percent);
  return { used, limit, unlimited: false, percent, remainingPct };
}

function fmt(n, unit) {
  if (typeof n !== 'number') return `${n}${unit ? ' ' + unit : ''}`;
  const rounded = Number.isInteger(n) ? n : Math.round(n * 100) / 100;
  return `${rounded.toLocaleString()}${unit ? ' ' + unit : ''}`;
}
